import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { HomePhase } from "@/components/v2/home/types";

const PHASE_ORDER: HomePhase[] = [
  "poll_inactive",
  "poll_open",
  "convocatoria",
  "post_kickoff",
  "completed",
];

/**
 * Always-on rail showing where this week's loop stands — idle -> poll ->
 * convocatoria -> kickoff -> result. New orientation element: previously the
 * phase card was the *only* signal of progress; now the whole week's shape
 * is visible at a glance before reading any card content.
 */
export function HomeWeekTracker({ current }: { current: HomePhase }) {
  const { t } = useTranslation();

  const labels: Record<HomePhase, string> = {
    poll_inactive: t("v2.home.trackerIdle"),
    poll_open: t("v2.home.trackerPollOpen"),
    convocatoria: t("v2.home.trackerConvocatoria"),
    post_kickoff: t("v2.home.trackerPostKickoff"),
    completed: t("v2.home.trackerCompleted"),
  };

  const currentIdx = PHASE_ORDER.indexOf(current);

  return (
    <div className="space-y-1.5">
      <div
        role="list"
        aria-label={t("v2.home.trackerAriaLabel")}
        className="flex items-center gap-1 sm:gap-2"
      >
        {PHASE_ORDER.map((phase, i) => {
          const state = i < currentIdx ? "done" : i === currentIdx ? "current" : "future";
          return (
            <div
              key={phase}
              role="listitem"
              aria-current={state === "current" ? "step" : undefined}
              className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
            >
              <div
                className={cn(
                  "h-1.5 w-full rounded-full transition-colors duration-300",
                  state === "future" ? "bg-border" : "bg-soccer-primary",
                  state === "current" && "shadow-[0_0_0_3px_rgba(53,219,113,0.18)]"
                )}
              />
              <span
                className={cn(
                  "hidden truncate text-[10px] font-bold uppercase tracking-wide sm:block",
                  state === "current"
                    ? "text-soccer-primary"
                    : state === "done"
                      ? "text-foreground/60"
                      : "text-muted-foreground/50"
                )}
              >
                {labels[phase]}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-soccer-primary sm:hidden">
        {labels[current]}
      </p>
    </div>
  );
}
